import { OutlineElement } from '../../base/outline-element/outline-element';
import '../ghs-icon/ghs-icon';
import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';

import {
  customElement,
  property,
  state,
  queryAssignedNodes,
} from 'lit/decorators.js';
import componentStyles from './ghs-tabs.css.lit';
import { MobileController } from '../../controllers/mobile-controller';
import { Orientation } from '../../base/outline-element/utils/types';

/**
 * The Tabs component
 *
 * @element ghs-tabs
 * @slot defaultSlot - Contains the <ghs-tab-nav> and <ghs-tab-panel> content.
 */
@customElement('ghs-tabs')
export class GhsTabs extends OutlineElement {
  private mobileController = new MobileController(this);
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The label text.
   * @param label
   */
  @property({ type: String })
  label = '';

  /**
   * The orientation.
   * @param orientation
   */
  @property({ type: String, reflect: true })
  orientation: Orientation = 'horizontal';

  /**
   * String of active/open panel ID.
   */
  @state() active: string | null = '';
  /**
   * String of active/open panel ID.
   */
  @state() activeElement: HTMLElement;

  @state() mobileIsOpen = false;
  /**
   * ref to <ghs-tab-panel> in panel slot.
   */
  @queryAssignedNodes('panel', true)
  panels: HTMLSlotElement[];

  /**
   * ref to <ghs-tab-nav> in nav slot.
   */
  @queryAssignedNodes('nav', true)
  navs: HTMLSlotElement[];

  handleDropdownTrigger() {
    this.mobileIsOpen = !this.mobileIsOpen;
  }

  /**
   * Takes the element's unique-id attribute of content <div>
   * to maintain state list of active/open panels.
   */
  setActive(e: PointerEvent) {
    const element = e.target as HTMLElement;
    if (element.tagName.toLowerCase() !== 'ghs-tab-nav') {
      return;
    }
    if (!element.hasAttribute('active')) {
      // Removing all the actives here to prevent multiple updates.
      this.navs.map(nav => {
        nav.removeAttribute('active');
      });
      this.panels.map(panel => {
        panel.removeAttribute('active');
      });
      element.setAttribute('active', 'active');
      this.activeElement = element;
    }
    const contentId = element.getAttribute('unique-id');
    this.mobileController.isMobile && (this.mobileIsOpen = !this.mobileIsOpen);
    return (this.active = contentId);
  }

  firstUpdated() {
    super.firstUpdated();

    // Set first index's unique-id to active state variable if it exists.
    if (this.navs[0]) {
      this.activeElement = this.navs[0];
      this.navs[0].setAttribute('active', 'active');
      this.panels[0].setAttribute('active', 'active');
    }
  }

  updated() {
    // Adding active attr also to prevent removing and adding multiple times.
    this.panels.map(panel => {
      const panelId = panel.getAttribute('unique-id');
      if (panelId !== null) {
        if (this.active === panel.getAttribute('unique-id')) {
          panel.setAttribute('active', 'active');
        }
      }
    });
  }

  render(): TemplateResult {
    const isMobile = this.mobileController.isMobile;
    const isOpen = this.mobileIsOpen ? 'is-open' : '';

    return html`
      <div class="tabs tabs--${this.orientation}">
        ${this.label
          ? html`<div class="tabs-heading">${this.label}</div>`
          : nothing}
        <div class="tabs-content">
          ${!isMobile
            ? html`
                <div class="tabs-nav" @click=${this.setActive} role="tablist">
                  <slot name="nav"></slot>
                </div>
              `
            : html`
                <div class="tabs-nav-dropdown-wrapper" role="tablist">
                  <button
                    class="tabs-nav-dropdown-trigger ${isOpen}"
                    data-toggle="dropdown"
                    aria-expanded=${this.mobileIsOpen ? 'true' : 'false'}
                    @click=${this.handleDropdownTrigger}
                  >
                    ${this.activeElement !== undefined
                      ? this.activeElement.getAttribute('label')
                      : nothing}
                    <ghs-icon name="chevron-right" class="${isOpen}"></ghs-icon>
                  </button>
                  <div
                    @click=${this.setActive}
                    class="tabs-nav-dropdown ${isOpen}"
                  >
                    <slot name="nav"></slot>
                  </div>
                </div>
              `}
          <div class="tabs-panels">
            <slot name="panel"></slot>
          </div>
        </div>
      </div>
    `;
  }
}
